import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  BarChart3,
  CircleAlert,
  CircleDot,
  FileText,
  GitBranchPlus,
  KanbanSquare,
  Layers,
  Lightbulb,
  Link,
  MoveUpRight,
  PanelLeft,
  PanelLeftClose,
  PauseCircle,
  PlayCircle,
  ShieldAlert,
  Spline,
  Workflow,
  Zap,
} from 'lucide-react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { PostItType, MapType, NodeType } from '../types';
import type { V186EngineeringTool } from './VsmCanvas/types';
import { useTheme } from '../context/ThemeContext';
import {
  getFluxoSidebarWidth,
  readFluxoSidebarExpanded,
  writeFluxoSidebarExpanded,
} from './fluxoSidebarLayout';

const FLUENT_ICON_SIZE = 18;
const FLUENT_ICON_STROKE = 1.5;

interface OfficeSidebarProps {
  mapType: MapType;
  onMapTypeChange: (type: MapType) => void;
  onSave?: () => void;
  onUndo?: () => void;
  onRedo?: () => void;
  onDuplicateSelected?: () => void;
  onDeleteSelected?: () => void;
  onAddActivity?: () => void;
  onAddDecision?: () => void;
  onAddStart?: () => void;
  onAddFinalCard?: () => void;
  onAddInventory?: () => void;
  onAddVSMNode?: (vsmIconKey: string) => void;
  onAddPostIt?: (type: PostItType) => void;
  onToggleManualArrow?: () => void;
  manualArrowMode?: boolean;
  onProjectSettings?: () => void;
  onOrgManager?: () => void;
  onAutoLayout?: () => void;
  onTogglePostIts?: () => void;
  postItsVisible?: boolean;
  selectedCardId?: string | null;
  selectedCardType?: NodeType;
  onAddCardConnected?: (type: NodeType, isYesPath?: boolean) => void;
  engineeringTool?: V186EngineeringTool;
  onEngineeringToolChange?: (tool: V186EngineeringTool) => void;
  onConsolidateFuture?: () => void;
  onRailWidthChange?: (width: number) => void;
}

interface ToolBtnProps {
  icon: React.ReactNode;
  label: string;
  description?: string;
  onClick?: () => void;
  active?: boolean;
  color?: string;
  disabled?: boolean;
  draggable?: boolean;
  dragType?: string;
  hasSelectedCard?: boolean;
  variant?: 'default' | 'fluent';
  expanded?: boolean;
}

function ToolTooltip({
  label,
  description,
  variant = 'default',
  enabled = true,
  expanded = false,
  children,
}: {
  label: string;
  description?: string;
  variant?: 'default' | 'fluent';
  enabled?: boolean;
  expanded?: boolean;
  children: React.ReactNode;
}) {
  if (!enabled) {
    return <>{children}</>;
  }
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState<{ top: number; left: number } | null>(null);
  const timeoutRef = useRef<number | null>(null);
  const triggerRef = useRef<HTMLDivElement | null>(null);

  const handleMouseEnter = () => {
    const rect = triggerRef.current?.getBoundingClientRect();
    if (rect) {
      setPosition({
        top: rect.top + rect.height / 2,
        left: rect.right + 10,
      });
    }
    timeoutRef.current = window.setTimeout(() => setOpen(true), 300);
  };

  const handleMouseLeave = () => {
    if (timeoutRef.current) {
      window.clearTimeout(timeoutRef.current);
    }
    setOpen(false);
  };

  useEffect(() => () => {
    if (timeoutRef.current) {
      window.clearTimeout(timeoutRef.current);
    }
  }, []);

  return (
    <div
      ref={triggerRef}
      className={`relative flex w-full min-w-0 ${expanded ? '' : 'justify-center'}`}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {children}
      {open && position && (
        <div
          className={
            variant === 'fluent'
              ? 'pointer-events-none fixed z-[120] -translate-y-1/2 rounded-md border border-[#e5e7eb] bg-white px-3 py-2 shadow-[0_4px_12px_rgba(15,23,42,0.08)]'
              : 'pointer-events-none fixed z-[120] -translate-y-1/2 rounded-[5px] border border-[var(--border-default)] bg-[#1e1e1e] dark:bg-[#1e1e1e] px-2.5 py-1.5 shadow-lg'
          }
          style={{ top: position.top, left: position.left }}
          role="tooltip"
        >
          <div
            className={
              variant === 'fluent'
                ? 'whitespace-nowrap text-[12px] font-semibold text-[#1f2937]'
                : 'whitespace-nowrap text-[11px] font-semibold text-[#e0e0e0]'
            }
          >
            {label}
          </div>
          {description && (
            <div
              className={
                variant === 'fluent'
                  ? 'mt-0.5 max-w-[220px] whitespace-normal text-[11px] text-[#6b7280]'
                  : 'mt-0.5 whitespace-nowrap text-[10px] text-[#8a8a8a]'
              }
            >
              {description}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

const ToolBtn: React.FC<ToolBtnProps> = ({
  icon,
  label,
  description,
  onClick,
  active,
  color,
  disabled,
  draggable,
  dragType,
  hasSelectedCard,
  variant = 'default',
  expanded = false,
}) => {
  const isFluent = variant === 'fluent';
  const isFluentExpanded = isFluent && expanded;
  const iconColor = isFluent
    ? 'text-[#475569]'
    : active
    ? 'text-[var(--ribbon-text-primary)]'
    : color || 'text-[var(--ribbon-text-secondary)] group-hover:text-[var(--ribbon-text-primary)]';

  const tooltipLabel = hasSelectedCard
    ? `${label} • conectar ao card`
    : draggable
    ? `${label} • arraste para o canvas`
    : label;

  const VSM_CONNECTOR_TYPES = new Set([
    'vsm_empurrado',
    'vsm_puxado',
    'vsm_info_manual',
    'vsm_info_eletro',
    'vsm_comunicacao',
  ]);

  return (
    <ToolTooltip
      label={tooltipLabel}
      description={description}
      variant={variant}
      enabled={!isFluentExpanded}
      expanded={isFluentExpanded}
    >
      <button
        type="button"
        title={tooltipLabel}
        aria-label={tooltipLabel}
        aria-pressed={active || undefined}
        onClick={disabled ? undefined : onClick}
        disabled={disabled}
        draggable={draggable && !disabled}
        onDragStart={(e) => {
          if (dragType) {
            const isVsmConnector = VSM_CONNECTOR_TYPES.has(dragType);
            if (isVsmConnector) {
              e.dataTransfer.setData('application/vsm-edge-drag', dragType);
              e.dataTransfer.effectAllowed = 'link';
              (window as any).__draggingCardType = null;
            } else {
              e.dataTransfer.setData('application/x-card-type', dragType);
              e.dataTransfer.effectAllowed = 'copy';
              (window as any).__draggingCardType = dragType;
            }

            // Criar miniatura de drag personalizada
            const preview = document.createElement('div');
            preview.style.cssText = 'position:fixed;top:-9999px;left:-9999px;pointer-events:none;';

            const isPostIt = ['action','problem','idea','data','risk'].includes(dragType);
            const isDecision = dragType === 'decisao';
            const isStart = dragType === 'card_inicial';
            const isFinal = dragType === 'card_final';
            const isInventory = dragType === 'inventory';
            const isVSM = dragType.startsWith('vsm_');

            if (isPostIt) {
              const colors: Record<string, { bg: string; border: string; label: string }> = {
                action:  { bg: '#bbf7d0', border: '#16a34a', label: 'Ação' },
                problem: { bg: '#fecaca', border: '#dc2626', label: 'Problema' },
                idea:    { bg: '#fef3c7', border: '#d97706', label: 'Ideia' },
                data:    { bg: '#dbeafe', border: '#2563eb', label: 'Dado' },
                risk:    { bg: '#fed7aa', border: '#ea580c', label: 'Risco' },
              };
              const c = colors[dragType];
              preview.innerHTML = `
                <div style="
                  width:72px;height:64px;
                  background:${c.bg};
                  border:1.5px solid ${c.border};
                  border-radius:5px;
                  box-shadow:0 6px 16px rgba(0,0,0,0.22),0 2px 4px rgba(0,0,0,0.12);
                  display:flex;align-items:center;justify-content:center;
                  font-size:9px;font-weight:700;color:${c.border};letter-spacing:0.02em;
                  transform:rotate(-3deg);opacity:0.92;position:relative;
                ">
                  ${c.label}
                  <div style="position:absolute;top:0;right:0;width:13px;height:13px;background:linear-gradient(225deg,rgba(0,0,0,0.12) 50%,transparent 50%);border-bottom-left-radius:3px;"></div>
                  <div style="position:absolute;bottom:12px;left:7px;right:7px;height:1.5px;background:${c.border};opacity:0.3;border-radius:1px;"></div>
                  <div style="position:absolute;bottom:7px;left:7px;right:14px;height:1.5px;background:${c.border};opacity:0.3;border-radius:1px;"></div>
                </div>`;
            } else if (isDecision) {
              preview.innerHTML = `
                <div style="width:80px;height:80px;position:relative;">
                  <svg width="80" height="80" style="position:absolute;top:0;left:0;overflow:visible;">
                    <polygon points="40,2 78,40 40,78 2,40"
                      fill="#0ea5e9" stroke="#0284c7" stroke-width="1.5" opacity="0.9"/>
                  </svg>
                  <div style="position:absolute;inset:0;display:flex;align-items:center;justify-content:center;font-size:8px;font-weight:700;color:#fff;text-shadow:0 1px 2px rgba(0,0,0,0.4);">Decisão</div>
                </div>`;
            } else if (isStart) {
              preview.innerHTML = `
                <div style="
                  width:76px;height:44px;
                  background:linear-gradient(135deg,#4ade80,#22c55e);
                  border:1.5px solid #16a34a;border-radius:22px;
                  box-shadow:0 4px 12px rgba(0,0,0,0.2);
                  display:flex;align-items:center;justify-content:center;
                  font-size:9px;font-weight:700;color:#fff;
                  text-shadow:0 1px 2px rgba(0,0,0,0.3);
                ">Início</div>`;
            } else if (isFinal) {
              preview.innerHTML = `
                <div style="
                  width:76px;height:44px;
                  background:linear-gradient(135deg,#f87171,#ef4444);
                  border:1.5px solid #dc2626;border-radius:22px;
                  box-shadow:0 4px 12px rgba(0,0,0,0.2);
                  display:flex;align-items:center;justify-content:center;
                  font-size:9px;font-weight:700;color:#fff;
                  text-shadow:0 1px 2px rgba(0,0,0,0.3);
                ">Fim</div>`;
            } else if (isInventory) {
              preview.innerHTML = `
                <div style="width:72px;height:72px;position:relative;">
                  <svg width="72" height="72" viewBox="0 0 72 72" style="position:absolute;top:0;left:0;">
                    <polygon points="36,4 68,68 4,68" fill="#f59e0b" stroke="#d97706" stroke-width="1.5" opacity="0.92"/>
                  </svg>
                  <div style="position:absolute;inset:0;display:flex;align-items:center;justify-content:center;padding-top:20px;font-size:7px;font-weight:700;color:#fff;text-shadow:0 1px 2px rgba(0,0,0,0.5);">Estoque</div>
                </div>`;
            } else if (isVSM) {
              const vsmLabels: Record<string, string> = {
                vsm_processo: 'Processo', vsm_operador: 'Operador', vsm_databox: 'Dados',
                vsm_comunicacao: 'Prog.', vsm_info_manual: 'S. Manual', vsm_info_eletro: 'Eletrôn.',
                vsm_empurrado: 'Push', vsm_puxado: 'Pull', vsm_automatizado: 'Auto',
                vsm_empilhador: 'Empilh.', vsm_caminhao: 'Caminhão', vsm_transport: 'Transp.', vsm_transporte: 'Transp.', vsm_vertical_flow: 'Fluxo Vert.',
                vsm_aviao: 'Avião', vsm_barco: 'Barco', vsm_trem: 'Trem',
                vsm_kanban_prod: 'K. Prod', vsm_kanban_ret: 'K. Ret.',
                vsm_kanban_lote: 'K. Lote', vsm_kanban_sinal: 'K. Sinal', vsm_kanban_posto: 'K. Posto',
vsm_estoque: 'Estoque', vsm_supermercado: 'Superm.', vsm_fifo: 'FIFO', vsm_emergencia: 'Emergência',
vsm_pulmao: 'Pulmão', vsm_externo: 'Cliente/Forn.', vsm_cliente: 'Cliente', vsm_fornecedor: 'Fornecedor', vsm_kaizen: 'Kaizen', vsm_vaver: 'Vá Ver', vsm_nivelamento: 'Nivelam.',
              };
              const lbl = vsmLabels[dragType] ?? dragType.replace('vsm_', '');
              const iconKey = dragType.replace('vsm_', '');
              const isVsmConnector = VSM_CONNECTOR_TYPES.has(dragType);
              const isVsmInventory = ['vsm_estoque', 'vsm_pulmao'].includes(dragType);
              preview.innerHTML = isVsmConnector ? `
                <div style="width:92px;height:34px;position:relative;">
                  <svg width="92" height="34" viewBox="0 0 92 34" style="position:absolute;inset:0;overflow:visible;">
                    <path d="M6 17 H76" fill="none" stroke="#06b6d4" stroke-width="2" stroke-dasharray="6 4"/>
                    <path d="M76 9 L88 17 L76 25" fill="none" stroke="#06b6d4" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                </div>` : isVsmInventory ? `
                <div style="width:72px;height:64px;position:relative;">
                  <svg width="72" height="64" viewBox="0 0 72 64" style="position:absolute;inset:0;overflow:visible;">
                    <polygon points="36,4 68,60 4,60" fill="rgba(6,182,212,0.08)" stroke="#0f172a" stroke-width="1.8" stroke-dasharray="5 3"/>
                  </svg>
                </div>` : `
                <div style="
                  width:100px;height:72px;
                  background:rgba(241,245,249,0.86);
                  border:1.5px dashed #334155;border-radius:4px;
                  box-shadow:0 6px 16px rgba(0,0,0,0.22);
                  display:flex;align-items:center;justify-content:center;
                  font-size:9px;font-weight:700;color:#1e293b;letter-spacing:0.04em;
                  text-transform:uppercase;
                ">
                  <img src="/assets/vsm/blueprint/${iconKey}.svg" alt="" style="max-width:76px;max-height:44px;object-fit:contain;filter:brightness(0) saturate(100%) invert(17%) sepia(15%) saturate(1233%) hue-rotate(176deg);" />
                  <span style="position:absolute;bottom:6px;left:0;right:0;text-align:center;">${lbl}</span>
                </div>`;
            } else {
              preview.innerHTML = `
                <div style="
                  width:120px;height:72px;
                  background:linear-gradient(160deg,#1e40af,#2563eb);
                  border:1.5px solid #1d4ed8;border-radius:8px;
                  box-shadow:0 6px 16px rgba(0,0,0,0.25);
                  display:flex;align-items:center;justify-content:center;
                  font-size:9px;font-weight:700;color:#fff;letter-spacing:0.04em;
                  text-shadow:0 1px 3px rgba(0,0,0,0.4);
                  text-transform:uppercase;
                ">Atividade</div>`;
            }

            document.body.appendChild(preview);
            const el = preview.firstElementChild as HTMLElement;
            const w = el?.offsetWidth || 80;
            const h = el?.offsetHeight || 60;
            e.dataTransfer.setDragImage(preview, w / 2, h / 2);
            requestAnimationFrame(() => document.body.removeChild(preview));
          }
        }}
        onDragEnd={() => {
          (window as any).__draggingCardType = null;
        }}
        className={
          isFluent
            ? `
              fluent-sidebar-nav-item group relative shrink-0 rounded-[4px] font-inherit transition-colors duration-150
              ${isFluentExpanded
                ? 'flex h-9 w-full items-center gap-0 px-2'
                : 'flex h-9 w-9 items-center justify-center'
              }
              ${disabled ? 'cursor-not-allowed opacity-40' : draggable ? 'cursor-grab active:cursor-grabbing' : 'cursor-pointer'}
              ${active
                ? 'bg-transparent text-[#475569]'
                : hasSelectedCard
                ? 'bg-transparent ring-1 ring-[#e2e8f0]'
                : 'text-[#475569] hover:bg-[#F1F5F9]'
              }
              focus:outline-none focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-[#94a3b8]/50 focus-visible:ring-offset-1
            `
            : `
              group relative flex flex-col h-auto w-full items-center justify-center gap-[2px] rounded-[5px] py-1.5
              font-inherit transition-all duration-150
              ${disabled ? 'cursor-not-allowed opacity-40' : draggable ? 'cursor-grab active:cursor-grabbing' : 'cursor-pointer'}
              ${active
                ? 'bg-[#223248] dark:bg-[#223248] border border-blue-500/40 border-l-4 border-l-cyan-500 dark:border-blue-400/40 brightness-110'
                : hasSelectedCard
                ? 'border border-[var(--border-default)] bg-[var(--ribbon-button-hover)]'
                : 'border border-transparent hover:bg-black/[0.06] dark:hover:bg-[#1C2A3A] hover:scale-[1.04]'
              }
              focus:outline-none focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-blue-500/30
            `
        }
      >
        {isFluent && active && (
          <span
            className="absolute left-0 top-1.5 bottom-1.5 w-[3px] rounded-r bg-[#0078D4]"
            aria-hidden
          />
        )}
        {hasSelectedCard && !active && (
          <div
            className={`absolute flex h-3 w-3 items-center justify-center rounded-full bg-[var(--text-secondary)] ${
              isFluent ? '-right-0.5 -top-0.5' : '-right-0.5 -top-0.5'
            }`}
          >
            <Link size={7} className="text-[var(--bg-surface)]" />
          </div>
        )}
        <span
          className={`
            fluent-sidebar-nav-item__icon flex shrink-0 items-center justify-center transition-colors duration-150
            ${isFluentExpanded ? 'h-9 w-9' : 'h-full w-full'}
            [&>svg]:shrink-0 ${isFluent ? '' : '[&>svg]:h-[18px] [&>svg]:w-[18px]'} ${iconColor}
          `}
        >
          {icon}
        </span>
        {isFluent && (
          <span className={`fluent-sidebar-nav-item__label truncate text-[12px] font-normal leading-none ${iconColor}`}>
            {label}
          </span>
        )}
      </button>
    </ToolTooltip>
  );
};

const GroupTitle = ({ children, isLight }: { children: React.ReactNode; isLight: boolean }) => (
  <div
    className={`w-full px-1 pb-1.5 pt-1 text-center text-[9px] font-semibold uppercase tracking-[0.06em] ${
      isLight ? 'text-slate-500' : 'text-slate-400'
    }`}
  >
    {children}
  </div>
);

const FluentSectionTitle = ({ children, expanded }: { children: React.ReactNode; expanded: boolean }) => (
  <div className={`fluent-sidebar-section-title ${expanded ? 'fluent-sidebar-section-title--expanded' : ''}`}>
    {children}
  </div>
);

const FluentSectionDivider = ({ expanded }: { expanded: boolean }) => (
  <div
    className={`fluent-sidebar-divider ${expanded ? 'fluent-sidebar-divider--expanded' : ''}`}
    role="separator"
    aria-hidden
  />
);

const GroupSpacer = () => <div className="h-[8px] shrink-0" />;

const VSMIconWrapper = ({ children }: { children: React.ReactNode }) => (
  <div className="flex h-[15px] w-[15px] items-center justify-center overflow-hidden">
    {children}
  </div>
);

// ── VSM node preview icons (match VsmContextToolbox NODE_PREVIEW) ─────
function serratedPath(cx: number, cy: number, r: number, teeth: number): string {
  const inner = r * 0.75;
  const points: string[] = [];
  for (let i = 0; i < teeth * 2; i++) {
    const angle = (i * Math.PI) / teeth - Math.PI / 2;
    const radius = i % 2 === 0 ? r : inner;
    points.push(`${cx + radius * Math.cos(angle)},${cy + radius * Math.sin(angle)}`);
  }
  return `M ${points.join(' L ')} Z`;
}

const VsmProcessIcon = () => (
  <svg width="28" height="22" viewBox="0 0 28 22" fill="none">
    <rect x="1" y="1" width="26" height="20" rx="1" stroke="currentColor" strokeWidth="1.5" />
    <line x1="1" y1="13" x2="27" y2="13" stroke="currentColor" strokeWidth="1" />
  </svg>
);

const VsmInventoryIcon = () => (
  <svg width="28" height="22" viewBox="0 0 28 22" fill="none">
    <polygon points="14,2 26,20 2,20" stroke="currentColor" strokeWidth="1.5" fill="var(--bg-surface)" />
    <text x="14" y="17" textAnchor="middle" fontSize="8" fontWeight="700" fill="currentColor">I</text>
  </svg>
);

const VsmExternalIcon = () => (
  <svg width="28" height="22" viewBox="0 0 28 22" fill="none">
    <path d={serratedPath(14, 11, 10, 8)} stroke="currentColor" strokeWidth="1.5" fill="var(--bg-surface)" />
  </svg>
);

const VsmTransportIcon = () => (
  <svg width="28" height="22" viewBox="0 0 28 22" fill="none">
    <rect x="2" y="6" width="16" height="10" rx="1" stroke="currentColor" strokeWidth="1.5" />
    <path d="M18,10 L18,16 L26,16 L26,12 L22,8 L18,8 Z" stroke="currentColor" strokeWidth="1.5" />
    <circle cx="7" cy="18" r="2.5" stroke="currentColor" strokeWidth="1" />
    <circle cx="21" cy="18" r="2.5" stroke="currentColor" strokeWidth="1" />
  </svg>
);

const VsmSupermarketIcon = () => (
  <svg width="28" height="22" viewBox="0 0 28 22" fill="none">
    <rect x="2" y="2" width="24" height="18" rx="1" stroke="currentColor" strokeWidth="1.5" />
    <line x1="2" y1="8" x2="26" y2="8" stroke="currentColor" strokeWidth="0.8" />
    <line x1="2" y1="13" x2="26" y2="13" stroke="currentColor" strokeWidth="0.8" />
    <line x1="2" y1="18" x2="26" y2="18" stroke="currentColor" strokeWidth="0.8" />
  </svg>
);

const VsmQueueIcon = () => (
  <svg width="28" height="22" viewBox="0 0 28 22" fill="none">
    <rect x="5" y="5" width="22" height="16" rx="1" stroke="currentColor" strokeWidth="1.2" fill="var(--bg-surface)" />
    <rect x="3" y="3" width="22" height="16" rx="1" stroke="currentColor" strokeWidth="1.2" fill="var(--bg-surface)" />
    <rect x="1" y="1" width="22" height="16" rx="1" stroke="currentColor" strokeWidth="1.2" fill="var(--bg-surface)" />
  </svg>
);

const VsmMeetingIcon = () => (
  <svg width="28" height="22" viewBox="0 0 28 22" fill="none">
    <rect x="2" y="2" width="24" height="18" rx="1" stroke="currentColor" strokeWidth="1.5" />
    <line x1="2" y1="8" x2="26" y2="8" stroke="currentColor" strokeWidth="1.5" />
    <circle cx="9" cy="15" r="2.5" stroke="currentColor" strokeWidth="1" />
    <circle cx="19" cy="15" r="2.5" stroke="currentColor" strokeWidth="1" />
  </svg>
);

const VsmBlockInfoIcon = () => (
  <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
    <circle cx="9" cy="9" r="6.5" stroke="currentColor" strokeWidth="1.7" />
    <line x1="4.7" y1="13.3" x2="13.3" y2="4.7" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" />
  </svg>
);

const VsmKaizenBurstToolIcon = () => (
  <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
    <path d="M9 1.5L10.7 6.5L15.2 4.2L13.2 9L16.5 12.5L11.6 12.8L9.8 16.5L7.2 13.2L2.5 15L4.7 10.5L1.5 7.5L6.5 6.8Z" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round" />
  </svg>
);

const VsmApprovalIcon = () => (
  <svg width="28" height="22" viewBox="0 0 28 22" fill="none">
    <polygon points="14,2 26,11 14,20 2,11" stroke="currentColor" strokeWidth="1.5" fill="var(--bg-surface)" />
    <path d="M9,11 L13,15 L19,8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const VsmFifoIcon = () => (
  <svg width="28" height="22" viewBox="0 0 28 22" fill="none">
    <rect x="1" y="5" width="20" height="12" rx="1" stroke="currentColor" strokeWidth="1.5" />
    <path d="M21,8 L27,11 L21,14 Z" fill="currentColor" />
    <line x1="1" y1="11" x2="21" y2="11" stroke="currentColor" strokeWidth="0.8" />
    <text x="10" y="15" textAnchor="middle" fontSize="6" fontWeight="700" fill="currentColor">FIFO</text>
  </svg>
);

const VsmClienteIcon = () => (
  <svg width="28" height="18" viewBox="0 0 28 18" fill="none">
    <rect x="1" y="1" width="26" height="16" rx="8" stroke="currentColor" strokeWidth="1.5" fill="var(--bg-surface)" />
    <circle cx="7" cy="9" r="2.5" fill="currentColor" opacity="0.6" />
    <line x1="12" y1="9" x2="21" y2="9" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
    <line x1="12" y1="6" x2="18" y2="6" stroke="currentColor" strokeWidth="1" strokeLinecap="round" opacity="0.5" />
  </svg>
);

/** Ícone SVG para preview da barra lateral no modo factory */
const VsmPngIcon = ({ src, alt }: { src: string; alt: string }) => (
  <img
    src={src}
    alt={alt}
    draggable={false}
    style={{ width: 24, height: 24, objectFit: 'contain', display: 'block', margin: '0 auto' }}
  />
);

// ── Additional VSM icons ───────────────────────────────────────────────────

/** Operador: circle with person symbol */
const VsmOperadorIcon = () => (
  <svg width="22" height="22" viewBox="0 0 22 22" fill="none">
    <circle cx="11" cy="11" r="9.5" stroke="currentColor" strokeWidth="1.5" fill="var(--bg-surface)" />
    <circle cx="11" cy="7.5" r="2.5" fill="currentColor" />
    <path d="M6.5,16.5 Q11,12 15.5,16.5" stroke="currentColor" strokeWidth="1.2" fill="currentColor" />
  </svg>
);

/** Databox: rectangle with data rows */
const VsmDataboxIcon = () => (
  <svg width="28" height="22" viewBox="0 0 28 22" fill="none">
    <rect x="1" y="1" width="26" height="20" rx="1" stroke="currentColor" strokeWidth="1.5" fill="var(--bg-surface)" />
    <line x1="1" y1="7" x2="27" y2="7" stroke="currentColor" strokeWidth="0.8" />
    <line x1="1" y1="13" x2="27" y2="13" stroke="currentColor" strokeWidth="0.8" />
    <text x="5" y="5.5" fontSize="4.5" fontWeight="700" fill="currentColor">C/T</text>
    <text x="5" y="11.5" fontSize="4.5" fontWeight="700" fill="currentColor">C/O</text>
    <text x="5" y="18" fontSize="4.5" fontWeight="700" fill="currentColor">OEE</text>
  </svg>
);

/** Comunicação: schedule box */
const VsmComunicacaoIcon = () => (
  <svg width="28" height="22" viewBox="0 0 28 22" fill="none">
    <rect x="1" y="1" width="26" height="20" rx="1" stroke="currentColor" strokeWidth="1.5" fill="var(--bg-surface)" />
    <line x1="1" y1="8" x2="27" y2="8" stroke="currentColor" strokeWidth="1.2" />
    <text x="14" y="6.5" textAnchor="middle" fontSize="4.5" fontWeight="700" fill="currentColor">PROG.</text>
    <line x1="4" y1="12" x2="24" y2="12" stroke="currentColor" strokeWidth="0.7" opacity="0.5" />
    <line x1="4" y1="16" x2="22" y2="16" stroke="currentColor" strokeWidth="0.7" opacity="0.5" />
  </svg>
);

/** Info Manual: elbow arrow */
const VsmInfoManualIcon = () => (
  <svg width="28" height="22" viewBox="0 0 28 22" fill="none">
    <polyline points="14,2 14,12 24,12" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" fill="none" strokeLinecap="round" />
    <polygon points="24,12 19,9 19,15" fill="currentColor" />
    <text x="2" y="20" fontSize="5" fill="currentColor" fontWeight="600">Manual</text>
  </svg>
);

/** Info Eletrônica: zigzag arrow */
const VsmInfoEletroIcon = () => (
  <svg width="28" height="22" viewBox="0 0 28 22" fill="none">
    <polyline points="2,11 7,5 14,11 21,5 26,11" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" fill="none" strokeLinecap="round" />
    <polygon points="26,11 21,8 21,14" fill="currentColor" />
  </svg>
);

/** Empurrado: segmented push arrow */
const VsmEmpurradoIcon = () => (
  <svg width="28" height="22" viewBox="0 0 28 22" fill="none">
    <rect x="1" y="7" width="18" height="8" fill="var(--bg-surface)" stroke="currentColor" strokeWidth="1.2" />
    <line x1="7" y1="7" x2="7" y2="15" stroke="currentColor" strokeWidth="0.8" />
    <line x1="13" y1="7" x2="13" y2="15" stroke="currentColor" strokeWidth="0.8" />
    <polygon points="19,5 27,11 19,17" fill="currentColor" />
  </svg>
);

/** Puxado: hollow pull arrow */
const VsmPuxadoIcon = () => (
  <svg width="28" height="22" viewBox="0 0 28 22" fill="none">
    <rect x="1" y="7" width="18" height="8" fill="var(--bg-surface)" stroke="currentColor" strokeWidth="1.2" />
    <polygon points="19,5 27,11 19,17" fill="var(--bg-surface)" stroke="currentColor" strokeWidth="1.2" />
  </svg>
);

/** Automatizado: arrow with dots */
const VsmAutomatizadoIcon = () => (
  <svg width="28" height="22" viewBox="0 0 28 22" fill="none">
    <line x1="2" y1="11" x2="22" y2="11" stroke="currentColor" strokeWidth="1.5" />
    <polygon points="22,11 17,8 17,14" fill="currentColor" />
    <circle cx="6" cy="7" r="2" fill="currentColor" />
    <circle cx="12" cy="7" r="2" fill="currentColor" />
    <circle cx="18" cy="7" r="2" fill="currentColor" />
  </svg>
);

/** Empilhador: forklift */
const VsmEmpilhadorIcon = () => (
  <svg width="28" height="22" viewBox="0 0 28 22" fill="none">
    <rect x="8" y="4" width="14" height="12" rx="1" stroke="currentColor" strokeWidth="1.2" fill="var(--bg-surface)" />
    <rect x="8" y="2" width="8" height="6" rx="1" stroke="currentColor" strokeWidth="1" fill="var(--bg-surface)" />
    <line x1="22" y1="4" x2="22" y2="16" stroke="currentColor" strokeWidth="1.2" />
    <line x1="22" y1="13" x2="28" y2="13" stroke="currentColor" strokeWidth="1.5" />
    <line x1="22" y1="16" x2="28" y2="16" stroke="currentColor" strokeWidth="1.5" />
    <circle cx="11" cy="20" r="2" stroke="currentColor" strokeWidth="1" fill="var(--bg-surface)" />
    <circle cx="19" cy="20" r="2" stroke="currentColor" strokeWidth="1" fill="var(--bg-surface)" />
  </svg>
);

/** Emergência: triangle with flag */
const VsmEmergenciaIcon = () => (
  <svg width="28" height="22" viewBox="0 0 28 22" fill="none">
    <polygon points="14,2 26,20 2,20" stroke="currentColor" strokeWidth="1.5" fill="var(--bg-surface)" />
    <line x1="14" y1="2" x2="14" y2="0" stroke="currentColor" strokeWidth="1.2" />
    <polygon points="14,0 20,3 14,6" fill="currentColor" />
    <text x="14" y="17" textAnchor="middle" fontSize="7" fontWeight="700" fill="currentColor">I</text>
  </svg>
);

/** Kanban Produção: pentagon card */
const VsmKanbanProdIcon = () => (
  <svg width="28" height="22" viewBox="0 0 28 22" fill="none">
    <polygon points="2,1 26,1 26,16 14,21 2,16" stroke="currentColor" strokeWidth="1.5" fill="var(--bg-surface)" />
    <line x1="2" y1="7" x2="26" y2="7" stroke="currentColor" strokeWidth="0.8" />
    <text x="14" y="5.5" textAnchor="middle" fontSize="4" fontWeight="700" fill="currentColor">K</text>
    <text x="14" y="13" textAnchor="middle" fontSize="4" fill="currentColor">PROD</text>
  </svg>
);

/** Kanban Retirada: hatched rectangle */
const VsmKanbanRetIcon = () => (
  <svg width="28" height="22" viewBox="0 0 28 22" fill="none">
    <defs>
      <pattern id="sb-hatch" width="4" height="4" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
        <line x1="0" y1="0" x2="0" y2="4" stroke="currentColor" strokeWidth="1" />
      </pattern>
    </defs>
    <rect x="1" y="1" width="26" height="20" fill="url(#sb-hatch)" />
    <rect x="1" y="1" width="26" height="20" fill="none" stroke="currentColor" strokeWidth="1.5" />
  </svg>
);

/** Caminhão: truck */
const VsmCaminhaoIcon = () => (
  <svg width="28" height="22" viewBox="0 0 28 22" fill="none">
    <rect x="1" y="5" width="14" height="11" rx="1" stroke="currentColor" strokeWidth="1.2" fill="var(--bg-surface)" />
    <path d="M15,8 L15,16 L27,16 L27,12 L23,8 Z" stroke="currentColor" strokeWidth="1.2" fill="var(--bg-surface)" />
    <circle cx="6" cy="18.5" r="2" stroke="currentColor" strokeWidth="1" fill="var(--bg-surface)" />
    <circle cx="22" cy="18.5" r="2" stroke="currentColor" strokeWidth="1" fill="var(--bg-surface)" />
  </svg>
);

/** Avião: airplane */
const VsmAviaoIcon = () => (
  <svg width="28" height="22" viewBox="0 0 28 22" fill="none">
    <ellipse cx="14" cy="11" rx="10" ry="3" stroke="currentColor" strokeWidth="1.2" fill="var(--bg-surface)" />
    <path d="M4,11 L8,4 L14,11 L20,4 L24,11" stroke="currentColor" strokeWidth="1" fill="var(--bg-surface)" />
    <path d="M6,14 L4,18 L14,15 L24,18 L22,14" stroke="currentColor" strokeWidth="0.8" fill="var(--bg-surface)" />
  </svg>
);

/** Barco: boat */
const VsmBarcoIcon = () => (
  <svg width="28" height="22" viewBox="0 0 28 22" fill="none">
    <path d="M4,14 Q14,20 24,14 L22,10 L6,10 Z" stroke="currentColor" strokeWidth="1.2" fill="var(--bg-surface)" />
    <rect x="10" y="4" width="8" height="6" rx="1" stroke="currentColor" strokeWidth="1" fill="var(--bg-surface)" />
    <line x1="14" y1="1" x2="14" y2="4" stroke="currentColor" strokeWidth="1.2" />
    <polygon points="14,1 20,4 14,7" fill="currentColor" opacity="0.4" />
  </svg>
);

/** Trem: train */
const VsmTremIcon = () => (
  <svg width="28" height="22" viewBox="0 0 28 22" fill="none">
    <rect x="4" y="3" width="20" height="14" rx="2" stroke="currentColor" strokeWidth="1.2" fill="var(--bg-surface)" />
    <rect x="7" y="5" width="5" height="5" rx="0.5" stroke="currentColor" strokeWidth="0.8" fill="none" />
    <rect x="16" y="5" width="5" height="5" rx="0.5" stroke="currentColor" strokeWidth="0.8" fill="none" />
    <line x1="1" y1="20" x2="27" y2="20" stroke="currentColor" strokeWidth="1.5" />
    <circle cx="9" cy="19" r="2.5" stroke="currentColor" strokeWidth="1" fill="var(--bg-surface)" />
    <circle cx="19" cy="19" r="2.5" stroke="currentColor" strokeWidth="1" fill="var(--bg-surface)" />
  </svg>
);

/** Fornecedor: serrated factory building (same as VsmExternalIcon) */
const VsmFornecedorIcon = () => (
  <svg width="28" height="22" viewBox="0 0 28 22" fill="none">
    <path d={serratedPath(14, 11, 10, 8)} stroke="currentColor" strokeWidth="1.5" fill="var(--bg-surface)" />
    <text x="14" y="13" textAnchor="middle" fontSize="5" fontWeight="700" fill="currentColor">SUP</text>
  </svg>
);


const VsmAccordion = ({ title, isOpen, onToggle, children }: any) => {
  return (
    <div className="flex flex-col mb-2 select-none px-[4px]">
      <div 
        className="flex items-center justify-between px-3 py-2 bg-gray-100 hover:bg-gray-200 cursor-pointer rounded-t-md transition-colors border border-gray-200 border-b-0"
        onClick={onToggle}
      >
        <span className="text-[11px] font-semibold text-gray-700 tracking-wide uppercase">{title}</span>
        {isOpen ? <ChevronDown size={14} className="text-gray-500"/> : <ChevronRight size={14} className="text-gray-500"/>}
      </div>
      <div 
        className={`overflow-hidden transition-all duration-300 ease-in-out bg-white border border-gray-200 rounded-b-md ${isOpen ? 'max-h-[800px] opacity-100' : 'max-h-0 opacity-0 border-t-0'}`}
      >
        <div className="flex flex-col gap-1 p-2">
          {children}
        </div>
      </div>
    </div>
  );
};

export default function OfficeSidebar(props: OfficeSidebarProps) {
  const { isDark } = useTheme();
  const [toolPage, setToolPage] = useState<1 | 2 | 3 | 4>(1);
  const [vsmPage, setVsmPage] = useState<'MAT' | 'INF'>('MAT');
  const [sidebarExpanded, setSidebarExpanded] = useState(readFluxoSidebarExpanded);
  const isFactory = props.mapType === 'FACTORY';
  const isOffice = props.mapType === 'OFFICE';
  const hasSelectedCard = !!props.selectedCardId;

  const toggleSidebarExpanded = useCallback(() => {
    setSidebarExpanded((prev) => {
      const next = !prev;
      writeFluxoSidebarExpanded(next);
      return next;
    });
  }, []);

  useEffect(() => {
    props.onRailWidthChange?.(getFluxoSidebarWidth(sidebarExpanded));
  }, [sidebarExpanded, props.onRailWidthChange]);

  const handleSmartAdd = useCallback((type: NodeType) => {
    if (hasSelectedCard && props.onAddCardConnected) {
      props.onAddCardConnected(type, true);
      return;
    }

    switch (type) {
      case 'atividade':
        props.onAddActivity?.();
        break;
      case 'decisao':
        props.onAddDecision?.();
        break;
      case 'card_inicial':
        props.onAddStart?.();
        break;
      case 'card_final':
        props.onAddFinalCard?.();
        break;
      case 'inventory':
        props.onAddInventory?.();
        break;
    }
  }, [hasSelectedCard, props]);

  const renderOfficeTools = () => {
    const ic = { size: FLUENT_ICON_SIZE, strokeWidth: FLUENT_ICON_STROKE };

    const navProps = { variant: 'fluent' as const, expanded: sidebarExpanded };

    return (
      <div className={`fluent-sidebar-panel ${sidebarExpanded ? 'fluent-sidebar-panel--expanded' : ''}`}>
        <FluentSectionTitle expanded={sidebarExpanded}>Fluxo</FluentSectionTitle>
        <div className="fluent-sidebar-tools">
          <ToolBtn
            {...navProps}
            icon={<PlayCircle {...ic} />}
            label="Início"
            description="Ponto de entrada do fluxo"
            onClick={() => handleSmartAdd('card_inicial')}
            draggable
            dragType="card_inicial"
          />
          <ToolBtn
            {...navProps}
            icon={<FileText {...ic} />}
            label="Processo"
            description="Executa uma etapa do processo"
            onClick={() => handleSmartAdd('atividade')}
            hasSelectedCard={hasSelectedCard}
            draggable
            dragType="atividade"
          />
          <ToolBtn
            {...navProps}
            icon={<GitBranchPlus {...ic} />}
            label="Decisão"
            description="Ramificação do fluxo"
            onClick={() => handleSmartAdd('decisao')}
            hasSelectedCard={hasSelectedCard}
            draggable
            dragType="decisao"
          />
          <ToolBtn
            {...navProps}
            icon={<PauseCircle {...ic} />}
            label="Fim"
            description="Finaliza o fluxo"
            onClick={() => handleSmartAdd('card_final')}
            hasSelectedCard={hasSelectedCard}
            draggable
            dragType="card_final"
          />
          <ToolBtn
            {...navProps}
            icon={<Workflow {...ic} />}
            label="Conector"
            description="Conectar cards manualmente"
            onClick={props.onToggleManualArrow}
            active={props.manualArrowMode}
          />
        </div>

        <FluentSectionDivider expanded={sidebarExpanded} />

        <FluentSectionTitle expanded={sidebarExpanded}>Notas</FluentSectionTitle>
        <div className="fluent-sidebar-tools">
          <ToolBtn
            {...navProps}
            icon={<Zap {...ic} />}
            label="Ação"
            description="Ação ou melhoria com ganho mensurável (Kaizen)"
            onClick={() => props.onAddPostIt?.('action')}
            draggable
            dragType="action"
          />
          <ToolBtn
            {...navProps}
            icon={<CircleAlert {...ic} />}
            label="Problema"
            description="Ponto de dor ou não conformidade"
            onClick={() => props.onAddPostIt?.('problem')}
            draggable
            dragType="problem"
          />
          <ToolBtn
            {...navProps}
            icon={<BarChart3 {...ic} />}
            label="Dado"
            description="Dado ou métrica relevante"
            onClick={() => props.onAddPostIt?.('data')}
            draggable
            dragType="data"
          />
          <ToolBtn
            {...navProps}
            icon={<Lightbulb {...ic} />}
            label="Idéia"
            description="Sugestão ou ideia de melhoria"
            onClick={() => props.onAddPostIt?.('idea')}
            draggable
            dragType="idea"
          />
          <ToolBtn
            {...navProps}
            icon={<ShieldAlert {...ic} />}
            label="Risco"
            description="Possível falha ou risco identificado"
            onClick={() => props.onAddPostIt?.('risk')}
            draggable
            dragType="risk"
          />
        </div>
      </div>
    );
  };

  const renderFactoryTools = () => (
    <>
      <GroupTitle isLight={!isDark}>Estratégia</GroupTitle>
      <div className="flex w-full flex-col items-center gap-[6px] px-[5px]">
        {/* Externo — serrated cloud (supplier/customer) */}
        <ToolBtn icon={
          <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
            <path d="M9,1.5 L10.6,4.2 L13.5,3.2 L13.2,6.2 L16,7.5 L13.8,9.5 L15,12.5 L12,12 L10.5,14.5 L9,12.5 L7.5,14.5 L6,12 L3,12.5 L4.2,9.5 L2,7.5 L4.8,6.2 L4.5,3.2 L7.4,4.2 Z" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round" />
          </svg>
        } label="Externo" description="Fornecedor ou cliente" onClick={() => handleSmartAdd('card_inicial')} draggable dragType="card_inicial" />
        {/* Processo — retângulo com linha de databox */}
        <ToolBtn icon={
          <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
            <rect x="1.5" y="3" width="15" height="12" rx="1" stroke="currentColor" strokeWidth="1.4" />
            <line x1="1.5" y1="10" x2="16.5" y2="10" stroke="currentColor" strokeWidth="1" />
          </svg>
        } label="Processo" description="Operação industrial" onClick={() => handleSmartAdd('atividade')} hasSelectedCard={hasSelectedCard} draggable dragType="atividade" />
        {/* Estoque — triângulo técnico */}
        <ToolBtn icon={
          <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
            <polygon points="9,2 17,16 1,16" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round" />
          </svg>
        } label="Estoque" description="Ponto de inventário" onClick={() => handleSmartAdd('inventory')} hasSelectedCard={hasSelectedCard} draggable dragType="inventory" />
        {/* Transporte — caminhão simplificado */}
        <ToolBtn icon={
          <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
            <rect x="1" y="5" width="10" height="8" rx="0.5" stroke="currentColor" strokeWidth="1.3" />
            <path d="M11,7.5 L11,13 L17,13 L17,10 L14.5,7.5 Z" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round" />
            <circle cx="4" cy="14.5" r="1.5" stroke="currentColor" strokeWidth="1.1" />
            <circle cx="14" cy="14.5" r="1.5" stroke="currentColor" strokeWidth="1.1" />
          </svg>
        } label="Transporte" description="Logística / transporte" onClick={() => handleSmartAdd('atividade')} hasSelectedCard={hasSelectedCard} draggable dragType="atividade" />
        {/* Supermercado — prateleiras */}
        <ToolBtn icon={
          <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
            <rect x="1.5" y="2" width="15" height="14" rx="0.5" stroke="currentColor" strokeWidth="1.3" />
            <line x1="1.5" y1="7" x2="16.5" y2="7" stroke="currentColor" strokeWidth="1" />
            <line x1="1.5" y1="11.5" x2="16.5" y2="11.5" stroke="currentColor" strokeWidth="1" />
          </svg>
        } label="Supermercado" description="Supermercado / FIFO puxado" onClick={() => handleSmartAdd('inventory')} hasSelectedCard={hasSelectedCard} draggable dragType="inventory" />
        {/* FIFO — seta horizontal com barras verticais */}
        <ToolBtn icon={
          <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
            <rect x="1" y="6" width="12" height="6" rx="0.5" stroke="currentColor" strokeWidth="1.3" />
            <line x1="5" y1="6" x2="5" y2="12" stroke="currentColor" strokeWidth="1" />
            <line x1="9" y1="6" x2="9" y2="12" stroke="currentColor" strokeWidth="1" />
            <path d="M13,9 L17,9 M15,6.5 L17,9 L15,11.5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        } label="FIFO" description="Fluxo puxado FIFO" onClick={() => handleSmartAdd('inventory')} hasSelectedCard={hasSelectedCard} draggable dragType="inventory" />
        {/* Decisão — losango */}
        <ToolBtn icon={
          <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
            <polygon points="9,1.5 16.5,9 9,16.5 1.5,9" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round" />
          </svg>
        } label="Decisão" description="Gate / ponto de inspeção" onClick={() => handleSmartAdd('decisao')} hasSelectedCard={hasSelectedCard} draggable dragType="decisao" />
        {/* Fim — círculo com ponto */}
        <ToolBtn icon={
          <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
            <circle cx="9" cy="9" r="7" stroke="currentColor" strokeWidth="1.4" />
            <circle cx="9" cy="9" r="3" fill="currentColor" />
          </svg>
        } label="Fim" description="Fim do fluxo produtivo" onClick={() => handleSmartAdd('card_final')} hasSelectedCard={hasSelectedCard} draggable dragType="card_final" />
        <ToolBtn icon={<Spline size={17} strokeWidth={1.5} />} label="Conector" description="Conectar cards manualmente" onClick={props.onToggleManualArrow} active={props.manualArrowMode} />
      </div>
    </>
  );

  const renderVSMAccordion = () => {
    const VSM_CONNECTOR_TYPES = new Set([
      'vsm_empurrado',
      'vsm_puxado',
      'vsm_info_manual',
      'vsm_info_eletro',
      'vsm_comunicacao',
    ]);

    const IC = isDark ? '#9fb2c7' : '#334155';
    // Todos os ícones num grid 32×32, strokeWidth uniforme 1.8, fill none
    const SW = "1.8";
    const mkSvg = (content: React.ReactNode) => (
      <svg viewBox="0 0 32 32" fill="none" style={{ width: 16, height: 16 }}>
        {content}
      </svg>
    );

    // Inline SVG icons — geometria fiel ao blueprint, grid 32×32 uniformizado
    const icons: Record<string, React.ReactNode> = {
      vsm_externo: mkSvg(
        // Fábrica: dentes de serra à esquerda + parede reta direita
        <path stroke={IC} strokeWidth={SW} strokeLinejoin="round" fill="none"
          d="M2 30 V10 L7 4 L12 10 L17 4 L22 10 V7 H30 V30 Z"/>
      ),
      vsm_processo: mkSvg(
        // Retângulo com faixa de título no topo
        <g stroke={IC} strokeWidth={SW} fill="none">
          <rect x="2" y="2" width="28" height="28" rx="1"/>
          <line x1="2" y1="10" x2="30" y2="10"/>
        </g>
      ),
      vsm_estoque: mkSvg(
        // Triângulo
        <path stroke={IC} strokeWidth={SW} strokeLinejoin="miter" fill="none"
          d="M16 3 L30 29 H2 Z"/>
      ),
      vsm_supermercado: mkSvg(
        // U aberto à esquerda com prateleiras
        <g stroke={IC} strokeWidth={SW} fill="none" strokeLinejoin="miter">
          <path d="M8 3 H28 V29 H8"/>
          <line x1="8" y1="12" x2="22" y2="12"/>
          <line x1="8" y1="20" x2="22" y2="20"/>
        </g>
      ),
      vsm_pulmao: mkSvg(
        // Dois pentes opostos interdigitados (pulmao/buffer)
        <g stroke={IC} strokeWidth={SW} fill="none" strokeLinecap="square" strokeLinejoin="miter">
          <path d="M2 2 V30 H12 V24 H6 V17 H12 V10 H6 V3 H12"/>
          <path d="M30 2 V30 H20 V24 H26 V17 H20 V10 H26 V3 H20"/>
        </g>
      ),
      vsm_puxado: mkSvg(
        // Seta circular de puxada
        <g stroke={IC} strokeWidth={SW} fill="none" strokeLinecap="round">
          <path d="M27 10 C30 18 24 28 16 28 C8 28 3 22 3 16 C3 10 8 4 16 4 C20 4 24 6 26 9"/>
          <path d="M22 4 L27 10 L21 12" strokeLinejoin="round"/>
        </g>
      ),
      vsm_nivelamento: mkSvg(
        // Caixa heijunka: grid 2×2 com letras OXOX
        <g stroke={IC} strokeWidth={SW} fill="none">
          <rect x="2" y="2" width="28" height="28"/>
          <line x1="16" y1="2" x2="16" y2="30" strokeWidth="1.2"/>
          <line x1="2"  y1="16" x2="30" y2="16" strokeWidth="1.2"/>
          <text x="9" y="13" textAnchor="middle" fontSize="7" fontWeight="700"
            fontFamily="monospace" fill={IC} stroke="none">O</text>
          <text x="23" y="13" textAnchor="middle" fontSize="7" fontWeight="700"
            fontFamily="monospace" fill={IC} stroke="none">X</text>
          <text x="9" y="26" textAnchor="middle" fontSize="7" fontWeight="700"
            fontFamily="monospace" fill={IC} stroke="none">O</text>
          <text x="23" y="26" textAnchor="middle" fontSize="7" fontWeight="700"
            fontFamily="monospace" fill={IC} stroke="none">X</text>
        </g>
      ),
      vsm_comunicacao: mkSvg(
        // Envelope/balão de fala com dobra no canto
        <g stroke={IC} strokeWidth={SW} fill="none" strokeLinejoin="miter">
          <path d="M3 5 H22 L29 12 V29 H3 Z"/>
          <path d="M22 5 V12 H29" strokeWidth="1.2"/>
          <line x1="8" y1="17" x2="24" y2="17" strokeWidth="1.2"/>
          <line x1="8" y1="22" x2="20" y2="22" strokeWidth="1.2"/>
        </g>
      ),
      vsm_kanban_prod: mkSvg(
        // Pentágono (kanban de produção)
        <g stroke={IC} strokeWidth={SW} fill="none" strokeLinejoin="miter">
          <path d="M2 2 H20 L30 14 V30 H2 Z"/>
          <path d="M20 2 V14 H30" strokeWidth="1.2"/>
        </g>
      ),
      vsm_kanban_ret: mkSvg(
        // Pentágono com hachura diagonal
        <g stroke={IC} strokeWidth={SW} fill="none" strokeLinejoin="miter">
          <path d="M2 2 H20 L30 14 V30 H2 Z"/>
          <path d="M20 2 V14 H30" strokeWidth="1.2"/>
          <g strokeWidth="1" opacity="0.6">
            <line x1="2"  y1="10" x2="10" y2="2"/>
            <line x1="2"  y1="20" x2="20" y2="2"/>
            <line x1="2"  y1="30" x2="28" y2="4"/>
            <line x1="8"  y1="30" x2="30" y2="8"/>
            <line x1="18" y1="30" x2="30" y2="18"/>
            <line x1="27" y1="30" x2="30" y2="27"/>
          </g>
        </g>
      ),
      vsm_kanban_lote: mkSvg(
        // Três kanbans pentagonais em leque + seta bidirecional
        <g stroke={IC} strokeWidth="1.3" fill="none" strokeLinejoin="miter">
          <path d="M2 3 H14 L20 10 V20 H2 Z"/>
          <path d="M6 3 H18 L24 10 V20 H6 Z"/>
          <path d="M10 3 H22 L28 10 V20 H10 Z"/>
          <line x1="1" y1="26" x2="31" y2="26" strokeWidth="1.3"/>
          <path d="M4 23 L1 26 L4 29" strokeLinejoin="round" fill="none"/>
          <path d="M28 23 L31 26 L28 29" strokeLinejoin="round" fill="none"/>
        </g>
      ),
      vsm_kanban_sinal: mkSvg(
        // Triângulo invertido (sinalização)
        <path stroke={IC} strokeWidth={SW} strokeLinejoin="miter" fill="none"
          d="M16 29 L2 5 H30 Z"/>
      ),
      vsm_kanban_posto: mkSvg(
        // Prateleira larga no topo com duas pernas (rack stand ∏)
        <path stroke={IC} strokeWidth={SW} fill="none" strokeLinejoin="miter"
          d="M2 2 H30 V16 H22 V30 H10 V16 H2 Z"/>
      ),
      vsm_kaizen: mkSvg(
        // Estrela explosão Kaizen
        <g stroke={IC} strokeWidth={SW} fill="none" strokeLinejoin="round">
          <path d="M16 2L19 11L27 7L24 16L30 21L21 22L19 30L14 24L5 28L8 20L1 15L11 12Z"/>
          <line x1="11" y1="16" x2="21" y2="16" strokeWidth="1.4"/>
          <line x1="16" y1="11" x2="16" y2="21" strokeWidth="1.4"/>
        </g>
      ),
      vsm_vaver: mkSvg(
        // Dois óculos (go & see)
        <g stroke={IC} strokeWidth={SW} fill="none">
          <circle cx="10" cy="16" r="7"/>
          <circle cx="22" cy="16" r="7"/>
          <line x1="17" y1="16" x2="15" y2="16" strokeWidth="1.4"/>
          <path d="M3 13 L2 16 L3 19" strokeWidth="1.2"/>
          <path d="M29 13 L30 16 L29 19" strokeWidth="1.2"/>
        </g>
      ),
    };

    // Shared drag helper
    const vsmDrag = (vsmType: string, alt: string) => (e: React.DragEvent) => {
      e.dataTransfer.setData('application/vsm-type', vsmType);
      if (VSM_CONNECTOR_TYPES.has(vsmType)) {
        e.dataTransfer.setData('application/vsm-edge-drag', vsmType);
        e.dataTransfer.effectAllowed = 'link';
        (window as any).__draggingCardType = null;
      } else {
        e.dataTransfer.setData('application/x-card-type', vsmType);
        e.dataTransfer.effectAllowed = 'copy';
        (window as any).__draggingCardType = vsmType;
      }
      const preview = document.createElement('div');
      preview.style.cssText = 'position:fixed;top:-9999px;left:-9999px;pointer-events:none;';
      preview.innerHTML = `
        <div style="width:80px;height:56px;display:flex;align-items:center;justify-content:center;
          background:rgba(15,23,42,0.88);border:1.5px dashed #06b6d4;border-radius:4px;
          box-shadow:0 8px 18px rgba(0,0,0,0.28);position:relative;">
          <span style="color:#9fb2c7;font:700 9px JetBrains Mono,monospace;text-transform:uppercase;letter-spacing:0.06em;">${alt}</span>
        </div>`;
      document.body.appendChild(preview);
      e.dataTransfer.setDragImage(preview, 40, 28);
      window.setTimeout(() => preview.remove(), 0);
    };

    const VsmBtn = ({
      alt, vsmType, onClick,
    }: { alt: string; vsmType: string; onClick?: () => void }) => (
      <ToolTooltip label={alt} enabled={!sidebarExpanded} expanded={sidebarExpanded}>
        <button
          type="button"
          draggable
          title={alt}
          aria-label={alt}
          onDragStart={vsmDrag(vsmType, alt)}
          onDragEnd={() => { (window as any).__draggingCardType = null; }}
          onClick={onClick ?? (VSM_CONNECTOR_TYPES.has(vsmType) ? props.onToggleManualArrow : () => handleSmartAdd(vsmType as any))}
          className={
            sidebarExpanded
              ? 'fluent-sidebar-nav-item group relative flex h-9 w-full shrink-0 cursor-grab items-center gap-0 rounded-[4px] border-0 bg-transparent px-2 font-inherit text-[#475569] transition-colors duration-150 hover:bg-[#F1F5F9] active:cursor-grabbing focus:outline-none focus-visible:ring-1 focus-visible:ring-[#94a3b8]/50'
              : 'group relative flex h-9 w-9 shrink-0 cursor-grab flex-col items-center justify-center gap-[2px] overflow-hidden rounded-sm border-0 border-b-2 border-b-transparent bg-transparent px-0.5 text-center text-[var(--ribbon-text-secondary)] transition-colors hover:bg-[var(--ribbon-button-hover)] hover:text-[var(--ribbon-text-primary)] active:cursor-grabbing active:bg-[var(--ribbon-button-active)] focus:outline-none'
          }
        >
          <span className={`fluent-sidebar-nav-item__icon flex shrink-0 items-center justify-center ${sidebarExpanded ? 'h-9 w-9' : ''}`}>
            {icons[vsmType]}
          </span>
          <span className="fluent-sidebar-nav-item__label truncate text-[12px] font-normal leading-none">
            {alt}
          </span>
        </button>
      </ToolTooltip>
    );

    const factoryNavProps = { variant: 'fluent' as const, expanded: sidebarExpanded };

    return (
      <div className={`fluent-sidebar-panel ${sidebarExpanded ? 'fluent-sidebar-panel--expanded' : ''}`}>
        <div className="fluent-sidebar-tools">
        <VsmBtn alt="Entidade"     vsmType="vsm_externo"      />
        <VsmBtn alt="Processo"     vsmType="vsm_processo"     />
        <VsmBtn alt="Estoque"      vsmType="vsm_estoque"      />
        <VsmBtn alt="Supermercado" vsmType="vsm_supermercado" />
        <VsmBtn alt="Pulmão"       vsmType="vsm_pulmao"       />
        <VsmBtn alt="Puxado"       vsmType="vsm_puxado"       />
        </div>
        <FluentSectionDivider expanded={sidebarExpanded} />
        <div className="fluent-sidebar-tools">
        <VsmBtn alt="Ctrl. Prod."  vsmType="vsm_nivelamento"  />
        <VsmBtn alt="Comunicação"  vsmType="vsm_comunicacao"  />
        <VsmBtn alt="Kanban Prod"  vsmType="vsm_kanban_prod"  />
        <VsmBtn alt="Kanban Ret."  vsmType="vsm_kanban_ret"   />
        <VsmBtn alt="Kanban Lote"  vsmType="vsm_kanban_lote"  />
        <VsmBtn alt="Kanban Sinal" vsmType="vsm_kanban_sinal" />
        <VsmBtn alt="Kanban Posto" vsmType="vsm_kanban_posto" />
        <VsmBtn alt="Kaizen"       vsmType="vsm_kaizen"       />
        <VsmBtn alt="Vá Ver"       vsmType="vsm_vaver"        />
        </div>
        <FluentSectionDivider expanded={sidebarExpanded} />
        <div className="fluent-sidebar-tools">
        <ToolBtn
          {...factoryNavProps}
          icon={<VsmBlockInfoIcon />}
          label="Bloquear Info"
          description="Clique em uma seta de informação"
          onClick={() => props.onEngineeringToolChange?.(props.engineeringTool === 'block_info' ? null : 'block_info')}
          active={props.engineeringTool === 'block_info'}
        />
        <ToolBtn
          {...factoryNavProps}
          icon={<KanbanSquare size={17} strokeWidth={1.5} />}
          label="Conexão Kanban"
          description="Crie uma seta puxada da direita para a esquerda"
          onClick={() => props.onEngineeringToolChange?.(props.engineeringTool === 'kanban_connection' ? null : 'kanban_connection')}
          active={props.engineeringTool === 'kanban_connection'}
        />
        <ToolBtn
          {...factoryNavProps}
          icon={<MoveUpRight size={17} strokeWidth={1.5} />}
          label="Sinal Puxada"
          description="Conecte PCPM → Expedição para criar seta de retirada kanban"
          onClick={() => props.onEngineeringToolChange?.(props.engineeringTool === 'pull_signal_connection' ? null : 'pull_signal_connection')}
          active={props.engineeringTool === 'pull_signal_connection'}
        />
        <ToolBtn
          {...factoryNavProps}
          icon={<VsmKaizenBurstToolIcon />}
          label="Kaizen Proj."
          description="Clique em um processo para projetar melhoria"
          onClick={() => props.onEngineeringToolChange?.(props.engineeringTool === 'kaizen' ? null : 'kaizen')}
          active={props.engineeringTool === 'kaizen'}
        />
        <ToolBtn
          {...factoryNavProps}
          icon={<CircleDot size={17} strokeWidth={1.5} />}
          label="Consolidar Futuro"
          description="Aplicar projeções e limpar marcações"
          onClick={props.onConsolidateFuture}
        />
        </div>
      </div>
    );
  }

  return (
      <div
        className={`fluent-sidebar-rail relative z-20 flex h-full flex-col overflow-hidden font-inherit ${
          sidebarExpanded ? 'fluent-sidebar-rail--expanded' : 'fluent-sidebar-rail--collapsed'
        }`}
        style={{
          border: 'none',
          margin: 0,
          padding: 0,
          background: isDark ? 'var(--officebar-bg)' : '#F8FAFC',
          color: isDark ? 'var(--ribbon-text-primary)' : '#1e293b',
          borderRight: isDark ? '1px solid var(--shell-divider)' : '1px solid #e5e7eb',
        }}
      >
        <div className="flex w-full flex-1 flex-col overflow-y-auto overflow-x-hidden scrollbar-hide pt-1">
          {isFactory ? (
            renderVSMAccordion()
          ) : (
            renderOfficeTools()
          )}
        </div>

        <div className={`fluent-sidebar-footer ${sidebarExpanded ? 'fluent-sidebar-footer--expanded' : ''}`}>
          <button
            type="button"
            className="fluent-sidebar-toggle"
            onClick={toggleSidebarExpanded}
            title={sidebarExpanded ? 'Recolher barra de ferramentas' : 'Expandir barra de ferramentas'}
            aria-label={sidebarExpanded ? 'Recolher barra de ferramentas' : 'Expandir barra de ferramentas'}
            aria-expanded={sidebarExpanded}
          >
            <span className="fluent-sidebar-toggle__icon">
              {sidebarExpanded ? (
                <PanelLeftClose size={16} strokeWidth={1.5} />
              ) : (
                <PanelLeft size={16} strokeWidth={1.5} />
              )}
            </span>
            <span className="fluent-sidebar-toggle__label">Recolher</span>
          </button>
        </div>
      </div>
  );
}
